import React, { useEffect, useRef } from 'react';
import './App.css';
import { observer } from 'mobx-react-lite';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { PrivateRoute } from '../common/components/base/private-route/private-route';
import { EiqManageLayout } from '../features/eiq-manage/layout/eiq-manage-layout';
import { Residents } from '../features/eiq-manage/pages/eiq-manage-residents/residents';
import { PermissionEnum } from '../common/enums/permission.enum';
import { LoginLayout } from '../features/login/layout/login-layout';
import { Login } from '../features/login/pages/login/login';
import { ForgotPassword } from '../features/login/pages/forgot-password/forgot-password';
import { SetNewPassword } from '../features/login/pages/set-new-password/set-new-password';
import { PasswordReset } from '../features/login/pages/password-reset/password-reset';
import userAuthStore from '../common/stores/user-auth/user-auth.store';
import { UserTypeEnum } from '../common/enums/user-type.enum';
import { Addresses } from 'features/eiq-manage/pages/eiq-manage-addresses/addresses';
import { Streets } from 'features/eiq-manage/pages/eiq-manage-addresses/tabs-content-pages/streets/streets';
import { Communities } from 'features/eiq-manage/pages/eiq-manage-addresses/tabs-content-pages/communities/communities';
import { EiqSecureLayout } from '../features/eiq-secure/layout/eiq-secure-layout';
import { EiqSecure } from '../features/eiq-secure/eiq-secure';
import { UserAccounts } from 'features/eiq-manage/pages/eiq-manage-accounts/tabs-content-pages/user-accounts';
import { Accounts } from 'features/eiq-manage/pages/eiq-manage-accounts/accounts';
import { AccountTypes } from 'features/eiq-manage/enums/account-type.enum';
import { Traffic } from '../features/eiq-secure/pages/traffic/traffic';
import { Configurations } from 'features/eiq-manage/pages/eiq-manage-configurations/configurations';
import { DefaultConfiguration } from 'features/eiq-manage/pages/eiq-manage-configurations/tabs-content-pages/default/default-configuration';
import { ServiceHoursConfiguration } from 'features/eiq-manage/pages/eiq-manage-configurations/tabs-content-pages/service-hours/service-hours-configuration';
import { KeysManagement } from '../features/eiq-manage/pages/eiq-manage-addresses/tabs-content-pages/keys-management/keys-management';
import { Keys } from '../features/eiq-secure/pages/keys/keys';
import { Payease } from '../features/eiq-secure/pages/payease/payease';
import { AdminVehicles } from 'features/eiq-manage/pages/eiq-manage-vehicles/vehicles';
import Features from 'features/eiq-manage/pages/eiq-manage-features/features';
import featureStore from 'common/stores/features/feature.store';
import AddressesManagement from 'features/eiq-manage/pages/eiq-manage-addresses/tabs-content-pages/addresses/addresses-management';
import { Citations } from 'features/eiq-manage/pages/eiq-manage-citations/citations';
import CitationConfigurations from 'features/eiq-manage/pages/eiq-manage-configurations/tabs-content-pages/citations/citations-configurations';
import { AccountInfo } from 'features/eiq-manage/pages/eiq-manage-accounts/tabs-content-pages/account-info';
import configurationStore from 'common/stores/configuration/configuration.store';
import zenDeskService from 'common/services/zendesk/zendesk.service';
import { loadZendeskWidget } from 'common/helpers/zendesk.helper';
import { Insight } from 'features/eiq-manage/pages/eiq-manage-insight/insight';
import { VehicleApprovals } from 'features/eiq-manage/pages/eiq-manage-vehicles/tab-content-pages/vehicle-approvals/vehicle-approvals';
import { AllVehicles } from 'features/eiq-manage/pages/eiq-manage-vehicles/tab-content-pages/all-vehicles/all-vehicles';
import { IncidentReports } from 'common/pages/incident-report/incident-reports/incident-reports';
import { AnonymousRegistration } from 'features/eiq-manage/pages/eiq-manage-anonymous-registration/anonymous-registration';
import { AllDevices } from 'features/eiq-manage/pages/eiq-manage-vehicles/tab-content-pages/all-devices/all-devices';
import { MassAlerts } from 'features/eiq-manage/pages/eiq-manage-mass-alerts/mass-alerts';
import { ExportData } from 'features/eiq-manage/pages/eiq-manage-configurations/tabs-content-pages/export/export-data';
import { HubConnection } from '@microsoft/signalr';
import { admissionNotificationHandlers } from 'features/eiq-secure/heplers/admission-notification-handlers';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';
import { GuestLimitations } from 'features/eiq-manage/pages/eiq-manage-configurations/tabs-content-pages/guests/guests-limitations';
import { CommunityInformation } from 'features/eiq-manage/pages/eqi-manage-community-information/community-information';
import { EIQVoiceConfiguration } from 'features/eiq-manage/pages/eqi-manage-community-information/tabs-content-pages/eiq-voice-configuration';
import { GatesConfiguration } from 'features/eiq-manage/pages/eqi-manage-community-information/tabs-content-pages/gates-configuration';
import { VerificationConfiguration } from 'features/eiq-manage/pages/eqi-manage-community-information/tabs-content-pages/verification-configuration';
import { CardAccessConfiguration } from 'features/eiq-manage/pages/eqi-manage-community-information/tabs-content-pages/card-access-configuration';
import { useNotifications } from '../common/context/NotificationsContext';
import { PlateScan } from '../features/eiq-manage/pages/eiq-manage-configurations/tabs-content-pages/plate-scan/plate-scan';
import { initializeSignalR } from '../common/helpers/signalr.helper';
import userPersonalizationStore from 'common/stores/user-personalization/user-personalization.store';
import idDocumentRecognitionContext from 'features/eiq-secure/stores/id-document-recognition/id-document-recognition-context';
import configurationService from 'common/services/configuration-service/configuration.service';
import { GateManagment } from 'features/eiq-secure/pages/gate-management/gate-management';
import DriveIdConfiguration from 'features/eiq-manage/pages/eiq-manage-configurations/tabs-content-pages/drive-id/drive-id-configuration';
import WelcomeEmailsConfiguration from 'features/eiq-manage/pages/eqi-manage-community-information/tabs-content-pages/welcome-emails-configuration';
import { TrafficLogArchiveConfiguration } from 'features/eiq-manage/pages/eqi-manage-community-information/tabs-content-pages/traffic-log-archive';
import { Reports } from 'common/pages/reports';
import { CreateIncidentReport } from 'common/pages/incident-report/create-incident-report/create-incident-report';
import ViewIncidentReport from 'common/pages/incident-report/view-incident-report/view-incident-report';
import { DailyActivityRepors } from 'common/pages/daily-activity-reports/daily-activity-reports';
import { TrafficReport } from 'features/eiq-manage/pages/eqi-manage-community-information/tabs-content-pages/traffic-report';
import IntegrationEvents from 'features/eiq-manage/pages/eiq-manage-integration-events/integration-events';
import ActivityLogs from 'features/eiq-manage/pages/eiq-manage-activity-logs/activity-logs';

const App = ({ props }: any) => {
  const defaultConfiguration = configurationStore.configuration?.default;
  const connectionRef = useRef<HubConnection | null>(null);
  const { addNotification } = useNotifications();

  useEffect(() => {
    if (!userAuthStore.isLogin || !featureStore.isRealTimeNotificationsEnabled) {
      if (connectionRef.current) {
        connectionRef.current.stop();
        plateRecognitionContext.clear();
        connectionRef.current = null;
      }
      return;
    }

    if (userAuthStore.userType !== UserTypeEnum.SecurityOfficer) {
      return;
    }

    const initializeConnection = async () => {
      if (connectionRef.current) {
        await connectionRef.current.stop();
      }

      const newConnection = await initializeSignalR(
        'hubs/security-officer-notification-hub',
        admissionNotificationHandlers(addNotification),
      );
      plateRecognitionContext.clear();
      connectionRef.current = newConnection;
    };

    initializeConnection();

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, [userAuthStore.isLogin, featureStore.isRealTimeNotificationsEnabled, userAuthStore.userType]);

  useEffect(() => {
    if (userAuthStore.isLogin) {
      featureStore.requestFeatures();
      configurationStore.requestConfiguration();
      userPersonalizationStore.requestUserPersonalization();
    }
  }, []);

  useEffect(() => {
    if (!defaultConfiguration) {
      return;
    }

    if (userAuthStore.isLogin) {
      const zendeskApiKey = process.env.REACT_APP_ZENDESK_WIDGET_KEY;

      if (!zendeskApiKey) {
        console.log('ZENDESK is disabled');
        return;
      }

      loadZendeskWidget(zendeskApiKey, () => zenDeskService.login(userAuthStore.userType!, defaultConfiguration.communityName!));
    }
  }, [defaultConfiguration, userAuthStore.isLogin]);

  useEffect(() => {
    const communityName = configurationStore.configuration?.default.communityName;
    const userType = userAuthStore.userType;

    if (!communityName) {
      document.title = 'EntranceIQ';
      return;
    }

    if (userType === 'admin') {
      document.title = `EIQ manage: ${communityName}`;
    } else if (userType === 'security_officer') {
      document.title = `EIQ secure: ${communityName}`;
    } else {
      document.title = 'EntranceIQ';
    }
  }, [configurationStore.configuration, userAuthStore.isLogin, userAuthStore.userType]);

  useEffect(() => {
    if (userAuthStore.userType !== UserTypeEnum.SecurityOfficer || !featureStore.isDriveIdEnabled) {
      return;
    }

    const init = async () => {
      const settings = await configurationService.getDriveIdSettings();
      await idDocumentRecognitionContext.init(settings.licenseKey, settings.sdkLocation);
    };

    init();

    return () => {
      idDocumentRecognitionContext.cleanup();
    };
  }, [userAuthStore.userType, featureStore.isDriveIdEnabled]);

  return (
    <>
      <Router basename={process.env.PUBLIC_URL}>
        <Routes>
          <Route path="/registration" element={<AnonymousRegistration />} />
          {!userAuthStore.isLogin && <Route path="/*" element={<Navigate to="/login" replace={true} />} />}

          {/* Redirect after login START*/}
          {userAuthStore.isLogin &&
            (userAuthStore.userType === UserTypeEnum.Admin || userAuthStore.userType === UserTypeEnum.SuperAdmin) && (
              <Route path="/" element={<Navigate to={userAuthStore.prevPath || '/eiq-manage'} replace={true} />} />
            )}
          {/* Need to update back-end with SecurityOfficer user type in access token */}
          {userAuthStore.isLogin &&
            userAuthStore.userType !== UserTypeEnum.Admin &&
            userAuthStore.userType !== UserTypeEnum.SuperAdmin &&
            userAuthStore.userType !== UserTypeEnum.WebGuest && (
              <Route path="/" element={<Navigate to={userAuthStore.prevPath || '/eiq-secure'} replace={true} />} />
            )}
          {/* END Redirect after login */}

          <Route element={<LoginLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/set-new-password" element={<SetNewPassword />} />
            <Route path="/password-reset" element={<PasswordReset />} />
          </Route>

          {userAuthStore.userType === UserTypeEnum.Admin ? (
            <Route path="eiq-manage" element={<PrivateRoute userTypes={[UserTypeEnum.Admin]} element={<EiqManageLayout />} />}>
              <Route index element={<Navigate to="/eiq-manage/residents" replace={true} />} />
              {/*Example of inner permissions*/}
              {/*<Route path="residents" element={<PrivateRoute permissions={[PermissionEnum.ResidentView]} element={<Residents />} />} />*/}
              <Route path="residents" element={<Residents />} />
              {featureStore.isIncidentReporterEnabled && (
                <Route path="report" element={<Reports baseRoute="/eiq-manage/report" />}>
                  <Route index element={<Navigate to="/eiq-manage/report/incident-reports" replace={true} />} />
                  <Route path="incident-reports" element={<IncidentReports />} />
                  <Route path="incident-reports/create" element={<CreateIncidentReport baseRoute="/eiq-manage/report" />} />
                  <Route path="incident-reports/:reportId" element={<ViewIncidentReport baseRoute="/eiq-manage/report" />} />
                  {featureStore.isDailyActivityReportEnabled && (
                    <Route path="daily-activity-reports" element={<DailyActivityRepors showDeleteButton={true} canCreateNew={true} />} />
                  )}
                </Route>
              )}
              <Route path="non-resident-users" element={<div>non-residents-users</div>} />
              {featureStore.isInsightEnabled && <Route path="insight" element={<Insight />} />}
              {featureStore.isMassAlertsEnabled && <Route path="mass-notifications" element={<MassAlerts />} />}
              <Route path="addresses" element={<Addresses />}>
                <Route index element={<Navigate to="/eiq-manage/addresses/addresses-management" replace={true} />} />
                <Route path="addresses-management" element={<AddressesManagement />} />
                <Route path="streets" element={<Streets />} />
                <Route path="communities" element={<Communities />} />
                <Route path="keys-management" element={<KeysManagement />} />
              </Route>
              <Route path="personnel-access" element={<div>Personnel access</div>} />
              <Route path="security-and-auditing" element={<div>Security and auditing</div>} />
              <Route path="traffic" element={<Traffic key={'traffic'} isArchive={false} basePath="eiq-manage" />} />
              <Route path="traffic/archive" element={<Traffic key={'traffic-archive'} isArchive={true} basePath="eiq-manage" />} />
              <Route path="system-configuration" element={<Configurations />}>
                <Route index element={<Navigate to="/eiq-manage/system-configuration/default" replace={true} />} />
                <Route path="general" element={<div>General</div>} />
                <Route path="default" element={<DefaultConfiguration />} />
                <Route path="service-hours" element={<ServiceHoursConfiguration />} />
                <Route path="export" element={<ExportData />} />
                {featureStore.isGuestLimitationsEnabled && <Route path="guest-limitations" element={<GuestLimitations />} />}
                {featureStore.isTrafficLogixEnabled && <Route path="citations" element={<CitationConfigurations />} />}
                {featureStore.isPlateScanEnabled && <Route path="plate-scan" element={<PlateScan />} />}
                {featureStore.isDriveIdEnabled && <Route path="drive-id" element={<DriveIdConfiguration />} />}
              </Route>
              <Route path="account-management" element={<Accounts />}>
                <Route index element={<Navigate to="/eiq-manage/account-management/account-info" replace={true} />} />
                <Route path="account-info" element={<AccountInfo />} />
              </Route>
              <Route path="vehicles" element={<AdminVehicles />}>
                <Route index element={<Navigate to="/eiq-manage/vehicles/approvals" replace={true} />} />
                <Route path="all" element={<AllVehicles />} />
                <Route path="approvals" element={<VehicleApprovals />} />
                <Route path="credentials" element={<AllDevices />} />
              </Route>
              {featureStore.isTrafficLogixEnabled && <Route path="citations" element={<Citations />} />}
            </Route>
          ) : (
            <Route path="eiq-manage" element={<PrivateRoute userTypes={[UserTypeEnum.SuperAdmin]} element={<EiqManageLayout />} />}>
              <Route index element={<Navigate to="/eiq-manage/account-management" replace={true} />} />
              <Route path="account-management" element={<Accounts />}>
                <Route index element={<Navigate to="/eiq-manage/account-management/admins" replace={true} />} />
                <Route path="admins" element={<UserAccounts accountType={AccountTypes.Admin} />} />
                <Route path="security-officers" element={<UserAccounts accountType={AccountTypes.SecurityOfficer} />} />
              </Route>
              <Route path="feature-management" element={<Features />} />
              <Route path="community-information" element={<CommunityInformation />}>
                <Route index element={<Navigate to="/eiq-manage/community-information/eiq-voice" replace={true} />} />
                <Route path="eiq-voice" element={<EIQVoiceConfiguration />} />
                <Route path="card-access" element={<CardAccessConfiguration />} />
                <Route path="locations" element={<GatesConfiguration />} />
                <Route path="verification" element={<VerificationConfiguration />} />
                <Route path="welcome-emails" element={<WelcomeEmailsConfiguration />} />
                <Route path="traffic-log-archive" element={<TrafficLogArchiveConfiguration />} />
                <Route path="traffic-reports" element={<TrafficReport />} />
              </Route>
              <Route path="integration-events" element={<IntegrationEvents />} />
              <Route path="audit-logs" element={<ActivityLogs />} />
            </Route>
          )}

          <Route
            path="eiq-secure"
            element={
              <PrivateRoute
                permissions={[PermissionEnum.readResident, PermissionEnum.updateResident, PermissionEnum.writeResident]}
                element={<EiqSecureLayout />}
              />
            }>
            <Route index element={<EiqSecure />} />
            <Route
              path="traffic"
              element={
                <PrivateRoute
                  permissions={[PermissionEnum.readTrafficLog]}
                  element={<Traffic key={'traffic'} isArchive={false} basePath="eiq-secure" />}
                />
              }
            />
            <Route
              path="traffic/archive"
              element={
                <PrivateRoute
                  permissions={[PermissionEnum.readTrafficLog]}
                  element={<Traffic key={'traffic-archive'} isArchive={true} basePath="eiq-secure" />}
                />
              }
            />
            <Route path="keys" element={<PrivateRoute permissions={[PermissionEnum.readTrafficLog]} element={<Keys />} />} />
            {featureStore.isIncidentReporterEnabled && (
              <Route path="report" element={<Reports baseRoute="/eiq-secure/report" />}>
                <Route index element={<Navigate to="/eiq-secure/report/incident-reports" replace={true} />} />
                <Route path="incident-reports" element={<IncidentReports />} />
                <Route path="incident-reports/create" element={<CreateIncidentReport baseRoute="/eiq-secure/report" />} />
                <Route path="incident-reports/:reportId" element={<ViewIncidentReport baseRoute="/eiq-secure/report" />} />
                {featureStore.isDailyActivityReportEnabled && (
                  <Route path="daily-activity-reports" element={<DailyActivityRepors showDeleteButton={false} canCreateNew={true} />} />
                )}
              </Route>
            )}
            {featureStore.isGateControllerEnabled && (
              <>
                <Route path="gate-management" element={<GateManagment />} />
              </>
            )}
            <Route path="function-guests-list" element={<div>Function guests list</div>} />
            <Route path="reports" element={<div>Reports</div>} />
            <Route path="global-contractor-list" element={<div>Global contractor list</div>} />
            <Route path="system-configuration" element={<div>Settings</div>} />
          </Route>
          {featureStore.isPayEaseEnabled && (
            <Route
              path="payease"
              element={
                <PrivateRoute
                  permissions={[PermissionEnum.readResident, PermissionEnum.updateResident, PermissionEnum.writeResident]}
                  element={<Payease />}
                />
              }
            />
          )}
          <Route path="*" element={<p>There's nothing here: 404!</p>} />
        </Routes>
      </Router>
    </>
  );
};

export default observer(App);
